<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { computed } from "vue"
import { UiButton, UiCurrencyInput, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type PurchaseOrderItemPayload = {
  id?: number | null
  product_id: number | ""
  quantity: string
  unit_price_cents: string
  ipi: string
  icms: string
  pis: string
  cofins: string
  _destroy?: boolean
}

type PurchaseOrderPayload = {
  id?: number | null
  company_id: number | ""
  supplier_id: number | ""
  status: string
  expected_at: string
  notes: string
  items: PurchaseOrderItemPayload[]
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  purchaseOrder: PurchaseOrderPayload
  suppliers: Array<{ label: string; value: number }>
  products: Array<{ label: string; value: number }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  purchase_order: {
    company_id: props.purchaseOrder.company_id,
    supplier_id: props.purchaseOrder.supplier_id,
    expected_at: props.purchaseOrder.expected_at,
    notes: props.purchaseOrder.notes,
    items_attributes: props.purchaseOrder.items.map((item) => ({ ...item, _destroy: false })),
  },
})

const notify = useNotifications()
const visibleItems = computed(() => form.purchase_order.items_attributes.filter((item) => !item._destroy))
const fieldError = (field: string) => props.errors?.[field]?.[0]

function itemError(index: number, field: string) {
  return props.errors?.[`purchase_order_items.${index}.${field}`]?.[0] || props.errors?.[`items_attributes.${index}.${field}`]?.[0]
}

function addItem() {
  form.purchase_order.items_attributes.push({
    id: null,
    product_id: "",
    quantity: "1",
    unit_price_cents: "0",
    ipi: "0",
    icms: "0",
    pis: "0",
    cofins: "0",
    _destroy: false,
  })
}

function removeItem(index: number) {
  const item = form.purchase_order.items_attributes[index]
  if (!item) return

  if (item.id) {
    item._destroy = true
    return
  }

  form.purchase_order.items_attributes.splice(index, 1)
}

function submit() {
  const opts = { onSuccess: () => notify.success(props.mode === "create" ? "Pedido criado com sucesso" : "Pedido atualizado com sucesso") }
  if (props.mode === "create") {
    form.post("/purchase_orders", opts)
    return
  }

  form.put(`/purchase_orders/${props.purchaseOrder.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/purchase_orders" back-label="Voltar para pedidos de compra">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiSelect
          id="purchase-order-supplier"
          :model-value="form.purchase_order.supplier_id"
          label="Fornecedor"
          :options="suppliers"
          :error="fieldError('supplier') || fieldError('supplier_id')"
          @update:model-value="form.purchase_order.supplier_id = $event as number | ''"
        />

        <UiTextInput
          id="purchase-order-expected-at"
          v-model="form.purchase_order.expected_at"
          type="date"
          label="Previsao de entrega"
          :error="fieldError('expected_at')"
        />
      </div>

      <div>
        <label class="mb-1 block text-sm font-medium text-slate-700" for="purchase-order-notes">Observacoes</label>
        <textarea
          id="purchase-order-notes"
          v-model="form.purchase_order.notes"
          rows="3"
          class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 transition focus:border-slate-400 focus:outline-none"
          placeholder="Observacoes internas para compras"
        />
      </div>

      <section class="rounded-md border border-slate-200 p-4">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-slate-800">Itens do pedido</h2>
          <UiButton type="button" size="sm" variant="secondary" @click="addItem">Adicionar item</UiButton>
        </div>

        <div class="space-y-3">
          <div
            v-for="(item, index) in form.purchase_order.items_attributes"
            v-show="!item._destroy"
            :key="index"
            class="grid gap-3 rounded-md border border-slate-100 p-3 md:grid-cols-6"
          >
            <UiSelect
              :id="`purchase-order-item-product-${index}`"
              :model-value="item.product_id"
              label="Produto"
              :options="products"
              :error="itemError(index, 'product_id')"
              @update:model-value="item.product_id = $event as number | ''"
            />

            <UiTextInput
              :id="`purchase-order-item-quantity-${index}`"
              v-model="item.quantity"
              type="number"
              label="Quantidade"
              :error="itemError(index, 'quantity')"
            />

            <UiCurrencyInput
              :id="`purchase-order-item-price-${index}`"
              v-model="item.unit_price_cents"
              label="Preco unitario"
              :error="itemError(index, 'unit_price')"
            />

            <UiTextInput :id="`purchase-order-item-ipi-${index}`" v-model="item.ipi" type="number" label="IPI (%)" :error="itemError(index, 'ipi')" />

            <UiTextInput :id="`purchase-order-item-icms-${index}`" v-model="item.icms" type="number" label="ICMS (%)" :error="itemError(index, 'icms')" />

            <UiTextInput :id="`purchase-order-item-pis-${index}`" v-model="item.pis" type="number" label="PIS (%)" :error="itemError(index, 'pis')" />

            <div class="flex items-end gap-2">
              <UiTextInput :id="`purchase-order-item-cofins-${index}`" v-model="item.cofins" type="number" label="COFINS (%)" :error="itemError(index, 'cofins')" />
              <UiButton type="button" size="sm" variant="danger" @click="removeItem(index)">Remover</UiButton>
            </div>
          </div>

          <p v-if="visibleItems.length === 0" class="text-sm text-slate-500">Adicione ao menos um item ao pedido.</p>
          <p v-if="fieldError('base')" class="text-sm text-rose-600">{{ fieldError('base') }}</p>
        </div>
      </section>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar pedido" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/purchase_orders" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>
